<template>
	<v-menu @update:model-value="getConnectableSessions" location="end">
		<template v-slot:activator="{ props }">
			<v-btn variant="tonal" :color="buttonColor" :loading="loading" v-bind="props">
				{{ buttonText }}
			</v-btn>
		</template>
		<v-card min-width="250" elevation="3">
			<v-list width="250">
				<v-list-item
					v-for="session in connectableSessions"
					:key="session.id"
					@click="() => connectAlternisSession(session.id, session.label)"
				>
					<v-list-item-title>{{ session.label }}</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useDomainStore } from "../../../store/domain";
import type { Session } from "../../../store/alternis_new";
import { api } from "@lr-notitia/notitia-operator/client";
import router from "../../../router";

const AOKPlusDomainId = "c8b7bf72-f668-42af-89d8-4496d21bba18";

const domainStore = useDomainStore();
const dataspaceId = router.currentRoute.value.params.dataspaceId as string;

const connectableSessions = ref<Session[]>([]);
const loading = ref(false);
const status = ref<"idle" | "success" | "error">("idle");

const buttonColor = computed(() => {
	if (status.value === "success") return "success";
	if (status.value === "error") return "error";
	return "primary";
});

const buttonText = computed(() => {
	if (loading.value) return "Creating...";
	if (status.value === "success") return "Success!";
	if (status.value === "error") return "Failed";
	return "CONNECT ALTERNIS SESSION";
});

async function getConnectableSessions() {
	const response = await fetch("/api/aokplus/connectable-sessions", {
		method: "GET",
		headers: {
			accept: "application/json",
		},
	}).catch((err) => {
		console.error("Error fetching sessions:", err);
		return null;
	});

	if (!response?.ok) {
		console.error("Failed to fetch sessions:", response?.statusText);
		return [];
	}

	const sessions = await response.json();
	connectableSessions.value = sessions;
	return sessions;
}

async function connectAlternisSession(sessionId: string, label: string) {
	loading.value = true;
	status.value = "idle";

	const url = `/api/v1.0/${domainStore.domain.id}/external/newWorkspace`;

	let templateId = "";
	let name = "";
	let description = "";
	let id = "";

	if (domainStore.domain.id === AOKPlusDomainId) {
		templateId = "723cdcd9-0526-4496-bb9e-fd48a94370c3"; // "FM" Template Workspace
		name = "Alternis " + label;
		description =
			"created by: " +
			domainStore.account.firstname +
			" " +
			domainStore.account.lastname +
			" at " +
			new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" });
		id = "new_" + sessionId;
	}

	const body = {
		dataspaceId,
		templateId,
		name,
		description,
		id,
	};

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${domainStore.authToken}`,
			},
			body: JSON.stringify(body),
		});

		const data = await response.json();

		if (response.ok) {
			console.log("Workspace created:", data);
			status.value = "success";

			setTimeout(() => {
				api()
					.domain(domainStore.domain.id)
					.workspace(id)
					.alternis()
					.postRtpLanguage(0, "hard-locked-de")
					.catch((err: any) => {
						console.error("Error posting rtp language:", err);
					});
				api()
					.domain(domainStore.domain.id)
					.workspace(id)
					.alternis()
					.postRtpLanguage(1, "hard-locked-de")
					.catch((err: any) => {
						console.error("Error posting rtp language:", err);
					});
			}, 1_000); // TODO: experimental timeout, I think this happens too fast

			// Reset status after 2 seconds
			setTimeout(() => {
				status.value = "idle";
			}, 2000);
			// Redirect to the new workspace
			router.push("/" + domainStore.domain.id + "/template/" + dataspaceId + "/template/" + id);
		} else {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
	} catch (err) {
		console.error("Error creating workspace:", err);
		status.value = "error";
		// Reset status after 3 seconds
		setTimeout(() => {
			status.value = "idle";
		}, 3000);
	} finally {
		loading.value = false;
	}
}
</script>
